import { aggregateEvents } from '@onward-delivery/core';
import { MANIFEST_SEARCHABLE } from './constants';

// Receiving dates and arrival estimates are stored as midnight UTC standing in for a calendar day,
// so comparing them in local time shifts the day west of GMT.
export const utcDay = (value) => {
    if (!value) return null;
    const date = value instanceof Date ? value : new Date(value);
    return Number.isNaN(date.getTime()) ? null : date.toISOString().slice(0, 10);
};

// When the freight is expected at the dock. aggregateEvents folds `event.x || acc.x` in list order,
// so the LAST non-null estimate wins — a re-estimated arrival beats the original. Falls back to the
// order field for orders with no warehouse event yet.
export const expectedArrival = (order) => {
    const warehouse = aggregateEvents(order?.wh_events || []);
    return warehouse?.est_received_date || order?.warehouse_estimated_delivery_date || null;
};

export const receivedAt = (order) => aggregateEvents(order?.wh_events || [])?.received_date || null;

// Est. Received Date vs the manifest's receiving date, as UTC days. Either side missing is false
// for both callers below: an order with no estimate is neither "on this date" nor "not due yet".
const comparedAsUtcDays = (order, receivingDate, compare) => {
    const expected = utcDay(expectedArrival(order));
    const manifestDay = utcDay(receivingDate);
    if (!expected || !manifestDay) return false;
    return compare(expected, manifestDay);
};

// What the "Filter by Receiving Date" toggle keeps: exact-day matches only, so it also hides
// overdue-but-unarrived freight that isNotDueYet never flags.
export const matchesReceivingDate = (order, receivingDate) =>
    comparedAsUtcDays(order, receivingDate, (expected, manifestDay) => expected === manifestDay);

// Freight not due until after the manifest's receiving date. Already-received orders are never
// "not due" — they are physically here, so they belong on whatever manifest reconciles them.
export const isNotDueYet = (order, receivingDate) => {
    if (receivedAt(order)) return false;
    return comparedAsUtcDays(order, receivingDate, (expected, manifestDay) => expected > manifestDay);
};

export const formatItemIdentifier = (item) => {
    if (item?.pallet) {
        return `Pallet ${item.pallet.pallet_name || item.pallet.pallet_number}`;
    }
    return `Item SKU: ${item?.sku || '--'}`;
};

export const formatItemDetail = (item) => {
    const description = item?.description || item?.item_type_details || 'No description';
    const skuPrefix = item?.pallet && item?.sku ? `${item.sku} - ` : '';
    return `${skuPrefix}${description} · Qty: ${item?.quantity ?? 1}`;
};

export const formatItemLabel = (item) => {
    const description = item?.description || item?.item_type_details || 'NO DESCRIPTION';

    if (item?.pallet) {
        const palletLabel = item.pallet.pallet_name || item.pallet.pallet_number;
        return `Pallet ${palletLabel} - ${description}${item.sku ? ` / ${item.sku}` : ''}`;
    }

    return item?.sku ? `Item SKU: ${item.sku} - ${description}` : description;
};

export const groupAddedItemsByOrder = (addedItems, itemsMap, itemQuantities) => {
    const groups = {};
    Object.keys(addedItems || {}).forEach((itemId) => {
        if (!addedItems[itemId]) return;
        const item = itemsMap?.[itemId];
        const orderId = item?.order?.order_id || itemId;
        if (!groups[orderId]) {
            groups[orderId] = { order: item?.order, items: [] };
        }
        const base = item || { item_id: itemId, sku: 'Unknown item' };
        // Display the quantity actually being added to the manifest (the split amount chosen in the
        // picker), not the order item's full quantity. Only override when a split was chosen.
        const addedQty = itemQuantities?.[itemId];
        groups[orderId].items.push(addedQty != null ? { ...base, quantity: addedQty } : base);
    });
    return Object.values(groups);
};

// Order rows (with item subRows) for the create/update manifest picker. Intangible items are
// informational lines with no physical footprint — they never get received or picked up, so a
// manifest holding one can never reach RECEIVED/COMPLETE. They're dropped here, per item, because
// non-service orders can carry intangible lines alongside physical ones. An order left with no
// physical items drops out of the picker entirely.
export const buildOrderPickerRows = (orders) => {
    return (orders || []).reduce((acc, order) => {
        const itemsByOrderId = (order?.itemsByOrderId || []).filter((item) => !item?.intangible);
        if (!itemsByOrderId.length) {
            return acc;
        }
        const pickableOrder = { ...order, itemsByOrderId };
        acc.push({
            ...pickableOrder,
            subRows: itemsByOrderId.map((item) => ({ ...item, order: pickableOrder })),
        });
        return acc;
    }, []);
};

// Whether every item of an order the picker can offer is on the manifest. Judged against physical
// items only, to match what buildOrderPickerRows offers — an order mixing physical and intangible
// lines would otherwise never read as complete.
export const isWholeOrderAdded = (order, addedItems) => {
    const pickableItems = (order?.itemsByOrderId || []).filter((item) => !item?.intangible);
    return pickableItems.length > 0 && pickableItems.every((item) => addedItems?.[item.item_id]);
};

// Manifest types that must be tied to exactly one shipper (drives partner_client_id + shipper-first UI).
export const isOnlySingleShipperType = (type) => type === 'INBOUND';

export const MANIFEST_INSERT_COLUMNS = [
    'manifest_id',
    'manifest_number',
    'name',
    'client_id',
    'partner_client_id',
    'type',
    'source',
    'origin',
    'destination',
    'route_id',
    'warehouse_id',
    'receiving_date',
    'reference_id',
    'share_manifest',
    'draft_items',
    'staging_zone',
    'receiving_zone',
];

// Groups dotted paths into a tree so attributes sharing a relationship prefix land under one node.
const buildPathTree = (attributes) => {
    const root = { leaves: [], children: new Map() };
    attributes.forEach((attr) => {
        const parts = attr.split('.');
        const field = parts.pop();
        const node = parts.reduce((current, part) => {
            if (!current.children.has(part)) current.children.set(part, { leaves: [], children: new Map() });
            return current.children.get(part);
        }, root);
        node.leaves.push(field);
    });
    return root;
};

// The conditions at one tree node: its own attributes, then one entry per relationship holding that
// relationship's conditions. A lone condition is not wrapped in `_or`.
const nodeConditions = (node, pattern) => [
    ...node.leaves.map((field) => ({ [field]: { _ilike: pattern } })),
    ...[...node.children].map(([relationship, child]) => {
        const conditions = nodeConditions(child, pattern);
        return { [relationship]: conditions.length === 1 ? conditions[0] : { _or: conditions } };
    }),
];

/**
 * Builds the Hasura `_or` filter for the Manifests search box: a case-insensitive substring match
 * on every searchable attribute. Dotted attributes nest through relationships, and attributes that
 * share a relationship path sit in one nested `_or` so Hasura runs one subquery per relationship.
 * @param {string} term Text typed by the user; surrounding whitespace is ignored.
 * @param {string[]} [attributes] Dotted attribute paths to match. Defaults to MANIFEST_SEARCHABLE.
 * @returns {{ _or: object[] } | null} The filter, or null when the term is empty.
 */
export const buildManifestSearchFilter = (term, attributes = MANIFEST_SEARCHABLE) => {
    const trimmed = term?.trim();
    if (!trimmed) return null;
    return { _or: nodeConditions(buildPathTree(attributes), `%${trimmed}%`) };
};

// Appends the trimmed, URL-encoded term as `?search=`; the bare path when the term is blank.
const withSearchParam = (path, term) => {
    const trimmed = term?.trim();
    return trimmed ? `${path}?search=${encodeURIComponent(trimmed)}` : path;
};

/**
 * Builds the link to a manifest's page. A non-empty search term rides along as `?search=` so the
 * manifest opens filtered to it.
 * @param {string} manifestId Manifest to open.
 * @param {string} [term] Search text from the Manifests list; trimmed and URL-encoded, omitted when blank.
 * @returns {string} Path such as `/manifests/abc?search=SN-1`.
 */
export const manifestHref = (manifestId, term) => withSearchParam(`/manifests/${manifestId}`, term);

/**
 * Builds the link back to the Manifests list, restoring its search when there is one.
 * @param {string} [term] Search text to restore; trimmed and URL-encoded, omitted when blank.
 * @returns {string} `/manifests`, or a path such as `/manifests?search=SN-1`.
 */
export const manifestsListHref = (term) => withSearchParam('/manifests', term);
